import { XStack, YStack, View, Text } from "tamagui";
import { useState } from "react";
import { RiFilter3Line, RiCheckLine, RiArrowRightSLine, RiHome5Line } from "react-icons/ri";
import { colors } from "@styles/colors";
import BottomSheet from "../ui/BottomSheet";
import type { AccountFilterOption } from "./types";

interface AccountFilterChipProps {
  accounts: ReadonlyArray<AccountFilterOption>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

/** 篩選帳戶 chip + 選帳戶底部彈出層 */
export default function AccountFilterChip({ accounts, selectedId, onSelect }: AccountFilterChipProps) {
  const [open, setOpen] = useState(false);
  const selected = accounts.find((a) => a.id === selectedId) ?? null;
  const accent = selected?.color ?? colors.sub;

  return (
    <>
      <XStack
        ai="center"
        gap="$2.5"
        bg={colors.light}
        br={12}
        px="$3.5"
        py="$3"
        cursor="pointer"
        bw={1.5}
        bc={selected ? accent : "transparent"}
        onPress={() => setOpen(true)}>
        <RiFilter3Line size={16} color={selected ? accent : colors.sub} />
        <YStack f={1} miw={0}>
          <Text fontSize={11} color={colors.hint} mb="$0.5">
            篩選帳戶
          </Text>
          {selected ? (
            <XStack ai="center" gap="$1.5">
              <View w={8} h={8} br={4} bg={accent} />
              <Text fontSize={13} fontWeight="700" color={colors.text}>
                {selected.label}
              </Text>
            </XStack>
          ) : (
            <Text fontSize={13} fontWeight="700" color={colors.text}>
              全部帳戶
            </Text>
          )}
        </YStack>
        {selected && (
          <XStack
            br={12}
            px="$2.5"
            py="$1"
            bg={colors.bg}
            cursor="pointer"
            onPress={(e: { stopPropagation?: () => void }) => {
              e.stopPropagation?.();
              onSelect(null);
            }}>
            <Text fontSize={11} fontWeight="600" color={colors.sub}>
              清除
            </Text>
          </XStack>
        )}
        <RiArrowRightSLine size={16} color={colors.hint} />
      </XStack>

      <BottomSheet open={open} onClose={() => setOpen(false)}>
        <YStack px="$5" pb="$3" bbw={1} bbc={colors.borderLight}>
          <Text fontSize={16} fontWeight="800" color={colors.text}>
            篩選帳戶
          </Text>
          <Text fontSize={11} color={colors.hint} mt="$1.5">
            選擇要查看哪個瓦斯帳戶的付費紀錄
          </Text>
        </YStack>

        <XStack
          ai="center"
          gap="$3"
          px="$5"
          py="$3.5"
          bbw={1}
          bbc={colors.borderLight}
          bg={selectedId === null ? colors.primaryBg : "transparent"}
          cursor="pointer"
          onPress={() => {
            onSelect(null);
            setOpen(false);
          }}>
          <View w={36} h={36} br={10} bg={colors.bg} ai="center" jc="center" flexShrink={0}>
            <RiFilter3Line size={16} color={colors.sub} />
          </View>
          <Text f={1} fontSize={14} fontWeight="700" color={colors.text}>
            全部帳戶
          </Text>
          {selectedId === null && <RiCheckLine size={18} color={colors.primary} />}
        </XStack>

        <YStack pb="$6">
          {accounts.length === 0 ? (
            <Text fontSize={13} color={colors.hint} ta="center" py="$6">
              尚無瓦斯帳戶
            </Text>
          ) : (
            accounts.map((a, i) => {
              const active = selectedId === a.id;
              const color = a.color ?? colors.primary;
              return (
                <XStack
                  key={a.id}
                  ai="center"
                  gap="$3"
                  px="$5"
                  py="$3.5"
                  bbw={i === accounts.length - 1 ? 0 : 1}
                  bbc={colors.borderLight}
                  bg={active ? `${color}10` : "transparent"}
                  cursor="pointer"
                  onPress={() => {
                    onSelect(a.id);
                    setOpen(false);
                  }}>
                  <View w={36} h={36} br={10} bg={`${color}18`} ai="center" jc="center" flexShrink={0}>
                    <RiHome5Line size={16} color={color} />
                  </View>
                  <YStack f={1} miw={0}>
                    <Text fontSize={14} fontWeight="700" color={colors.text}>
                      {a.label}
                    </Text>
                    <Text fontSize={11} color={colors.hint} mt="$0.5">
                      {a.vendor}
                      {a.count != null ? ` · ${a.count} 筆紀錄` : ""}
                    </Text>
                  </YStack>
                  {active && <RiCheckLine size={18} color={color} />}
                </XStack>
              );
            })
          )}
        </YStack>
      </BottomSheet>
    </>
  );
}
